import Phaser from 'phaser';
import { isCalmMode } from '../audio/CalmMode.js';

/**
 * Top-left HUD widget that shows the live arena ranking of every snake
 * in the current match. Top 10 by score (coins + totalBanked); if the
 * local player is outside the top 10 their row is appended at the bottom
 * after a divider so they can always see where they stand.
 *
 * Visual style mirrors the chest widget so the HUD reads as one family.
 */

interface LeaderboardEntry {
  id: string;
  name: string;
  score: number;
  isBot: boolean;
  isMe: boolean;
  alive?: boolean;
}

interface LeaderboardData {
  entries: LeaderboardEntry[];
  mySnakeId: string;
}

const W = 220;
const ROW_H = 16;
const HEADER_H = 22;
const PADDING_X = 12;
const TOP_N = 10;

const COLOR_PANEL = 0x0a0e18;
const PANEL_ALPHA = 0.55;
const COLOR_BORDER = 0x4a6a8c;

export class TopPlayersWidget {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Graphics;
  private headerLeft: Phaser.GameObjects.Text;
  private headerRight: Phaser.GameObjects.Text;
  private rows: Phaser.GameObjects.Text[] = [];
  private myRank: number = -1;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const x = 16;
    const y = 16;
    this.container = scene.add.container(x, y).setDepth(110).setScrollFactor(0);

    this.bg = scene.add.graphics();
    this.container.add(this.bg);

    this.headerLeft = scene.add.text(PADDING_X, 7, 'ARENA RANK', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#c8d0e0',
      fontStyle: 'bold',
    }).setOrigin(0, 0);
    this.headerLeft.setLetterSpacing?.(1);
    this.container.add(this.headerLeft);

    this.headerRight = scene.add.text(W - PADDING_X, 7, '0', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#666677',
    }).setOrigin(1, 0);
    this.container.add(this.headerRight);

    // Pre-create row text objects up to TOP_N (recycled on each update)
    for (let i = 0; i < TOP_N; i++) {
      const t = scene.add.text(PADDING_X, HEADER_H + i * ROW_H, '', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#888899',
      }).setOrigin(0, 0);
      this.rows.push(t);
      this.container.add(t);
    }

    this.drawPanel(false);
    this.layout(0);
  }

  /** Push fresh leaderboard data into the widget.
   *  Always renders exactly up to TOP_N rows. If the local player is
   *  outside the top N, the last row becomes the player at their actual
   *  rank (top 9 + you). */
  update(data: LeaderboardData): void {
    const myIndex = data.entries.findIndex(e => e.isMe);
    const playerOutsideTopN = myIndex >= TOP_N;
    // Build the visible rank list: top 9 + me, OR top 10 verbatim
    const visibleEntries: { rank: number; entry: LeaderboardEntry }[] = [];
    const head = playerOutsideTopN ? TOP_N - 1 : TOP_N;
    for (let i = 0; i < Math.min(head, data.entries.length); i++) {
      visibleEntries.push({ rank: i + 1, entry: data.entries[i] });
    }
    if (playerOutsideTopN) {
      visibleEntries.push({ rank: myIndex + 1, entry: data.entries[myIndex] });
    }

    // Header
    this.headerRight.setText(`${data.entries.length}`);

    for (let i = 0; i < TOP_N; i++) {
      const row = this.rows[i];
      if (i >= visibleEntries.length) {
        row.setVisible(false);
        continue;
      }
      const { rank, entry } = visibleEntries[i];
      row.setVisible(true);
      row.setText(this.formatRow(rank, entry.name, entry.score));
      row.setColor(this.colorFor(entry));
    }

    // Pulse if our rank improved
    const newRank = myIndex + 1;
    if (this.myRank > 0 && newRank > 0 && newRank < this.myRank) {
      this.pulse();
    }
    this.myRank = newRank;

    this.layout(visibleEntries.length);
  }

  private formatRow(rank: number, name: string, score: number): string {
    const rankStr = rank.toString().padStart(2, ' ');
    const safeName = (name || '?').slice(0, 14);     // keep rows from wrapping
    const scoreStr = score.toLocaleString();
    // Pad name column so scores align right at ~W-PADDING_X
    const nameCol = safeName.padEnd(15, ' ');
    const scoreCol = scoreStr.padStart(7, ' ');
    return `${rankStr}  ${nameCol}${scoreCol}`;
  }

  private colorFor(e: LeaderboardEntry): string {
    if (e.isMe) return '#ffd166';
    return e.isBot ? '#6b6f7d' : '#888899';
  }

  /** Resize panel based on how many rows are visible. */
  private layout(visibleRows: number): void {
    const h = HEADER_H + visibleRows * ROW_H + 8;
    // Re-position rows in case ROW_H changed
    for (let i = 0; i < this.rows.length; i++) {
      this.rows[i].setY(HEADER_H + i * ROW_H);
    }
    this.drawPanelHeight(h);
  }

  private pulse(): void {
    if (isCalmMode()) return; // no-stimulus: skip the rank-up scale pulse
    // Subtle scale pulse only — no color change
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1.04,
      scaleY: 1.04,
      duration: 130,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  private drawPanel(_glow: boolean): void {
    const h = (this as any).__lastH ?? (HEADER_H + TOP_N * ROW_H + 8);
    this.drawPanelHeight(h);
  }

  private drawPanelHeight(h: number): void {
    (this as any).__lastH = h;
    this.bg.clear();
    this.bg.fillStyle(COLOR_PANEL, PANEL_ALPHA);
    this.bg.fillRoundedRect(0, 0, W, h, 10);
    this.bg.lineStyle(1, COLOR_BORDER, 0.55);
    this.bg.strokeRoundedRect(0, 0, W, h, 10);
  }

  destroy(): void {
    this.container.destroy();
  }
}
